import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { Alert, Button, Spinner, ToggleSwitch } from 'flowbite-react'
import {
  HiChevronDown,
  HiChevronRight,
  HiExclamation,
  HiPencil,
  HiPlus,
  HiTrash,
} from 'react-icons/hi'
import toast from 'react-hot-toast'
import { toastApiError } from '../utils/apiError'
import {
  buildCreateBody,
  buildUpdateBody,
  createDepartment,
  deleteDepartment,
  flattenKnownKeywords,
  keywordKey,
  listDepartments,
  listDetectableObjects,
  reorderDepartments,
  updateDepartment,
} from '../utils/departments'
import EntityFormModal from './EntityFormModal'
import { ReorderButtons, StatusBadges, swap } from './EntityRowControls'
import AorList from './AorList'

/**
 * Department management for one authority. Always loads active AND inactive
 * departments so the reorder id list stays complete; `showInactive` (default on
 * for the management view) only controls display and whether reordering is
 * offered. Write controls are hidden entirely when `canWrite` is false.
 */
const DepartmentList = ({ token, authorityId, canWrite }) => {
  const [departments, setDepartments] = useState([])
  const [loading, setLoading] = useState(true)
  const [showInactive, setShowInactive] = useState(true)
  const [expandedId, setExpandedId] = useState(null)
  const [busyId, setBusyId] = useState(null)
  const [reordering, setReordering] = useState(false)
  const [modal, setModal] = useState({ open: false, mode: 'create', entity: null })
  const [knownKeywords, setKnownKeywords] = useState([])
  // Live AoR lists reported by expanded departments, keyed by department id.
  // These override the embedded `areas_of_responsibility` for keyword counting.
  const [aorOverrides, setAorOverrides] = useState({})

  const load = async () => {
    setLoading(true)
    try {
      const data = await listDepartments(token, authorityId, true)
      setDepartments(Array.isArray(data) ? data : [])
    } catch (error) {
      toastApiError(error, 'Failed to load departments')
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    load()
    setAorOverrides({})
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token, authorityId])

  // Known issue keywords are authority-independent reference data; load once.
  useEffect(() => {
    let cancelled = false
    listDetectableObjects(token)
      .then((data) => {
        if (!cancelled) setKnownKeywords(flattenKnownKeywords(data))
      })
      .catch(() => {
        // Non-fatal: the search box just won't suggest known keywords.
        if (!cancelled) setKnownKeywords([])
      })
    return () => {
      cancelled = true
    }
  }, [token])

  const handleAorsChange = useCallback((departmentId, aors) => {
    setAorOverrides((current) => ({ ...current, [departmentId]: aors }))
  }, [])

  // Count how many distinct AoRs (authority-wide) carry each keyword. A keyword
  // on more than one AoR is a "duplicate" and gets highlighted yellow.
  const duplicateKeys = useMemo(() => {
    const counts = new Map()
    for (const department of departments) {
      const aors = aorOverrides[department.id] ?? department.areas_of_responsibility ?? []
      for (const aor of aors) {
        const seen = new Set()
        for (const kw of aor.keywords ?? []) {
          const key = keywordKey(kw)
          if (!key || seen.has(key)) continue
          seen.add(key)
          counts.set(key, (counts.get(key) ?? 0) + 1)
        }
      }
    }
    const dupes = new Set()
    for (const [key, count] of counts) {
      if (count > 1) dupes.add(key)
    }
    return dupes
  }, [departments, aorOverrides])

  const clearSiblingCatchAll = (list, keepId) =>
    list.map((item) =>
      item.id === keepId ? item : item.is_catch_all ? { ...item, is_catch_all: false } : item
    )

  const handleCreate = async (form) => {
    const created = await createDepartment(token, authorityId, buildCreateBody(form))
    setDepartments((current) => {
      const next = [...current, created]
      return created.is_catch_all ? clearSiblingCatchAll(next, created.id) : next
    })
    setModal({ open: false, mode: 'create', entity: null })
    toast.success('Department created')
  }

  const handleEdit = async (form) => {
    const original = modal.entity
    const body = buildUpdateBody(form, original)
    if (Object.keys(body).length === 0) {
      setModal({ open: false, mode: 'edit', entity: null })
      return
    }
    const updated = await updateDepartment(token, authorityId, original.id, body)
    setDepartments((current) => {
      const next = current.map((item) =>
        item.id === updated.id
          ? { ...updated, areas_of_responsibility: item.areas_of_responsibility }
          : item
      )
      return updated.is_catch_all ? clearSiblingCatchAll(next, updated.id) : next
    })
    setModal({ open: false, mode: 'edit', entity: null })
    toast.success('Department updated')
  }

  const handleToggleActive = async (department) => {
    if (department.active && department.is_catch_all) {
      toast('Heads up: this is the authority catch-all department.', { icon: '⚠️' })
    }
    setBusyId(department.id)
    try {
      const updated = await updateDepartment(token, authorityId, department.id, {
        active: !department.active,
      })
      setDepartments((current) =>
        current.map((item) =>
          item.id === updated.id
            ? { ...updated, areas_of_responsibility: item.areas_of_responsibility }
            : item
        )
      )
    } catch (error) {
      toastApiError(error, 'Failed to update department')
    } finally {
      setBusyId(null)
    }
  }

  const handleDelete = async (department) => {
    const aorCount = department.areas_of_responsibility?.length ?? 0
    const catchAllWarning = department.is_catch_all
      ? '\n\nThis is the catch-all — unmatched reports will have nowhere to route until you set another.'
      : ''
    const aorWarning = aorCount > 0 ? `\n\nIts ${aorCount} area(s) of responsibility will be deleted too.` : ''
    if (
      !window.confirm(
        `Delete department "${department.name}"? This cannot be undone.${aorWarning}${catchAllWarning}`
      )
    ) {
      return
    }
    setBusyId(department.id)
    try {
      await deleteDepartment(token, authorityId, department.id)
      setDepartments((current) => current.filter((item) => item.id !== department.id))
      setAorOverrides((current) => {
        const next = { ...current }
        delete next[department.id]
        return next
      })
      if (expandedId === department.id) setExpandedId(null)
      toast.success('Department deleted')
    } catch (error) {
      toastApiError(error, 'Failed to delete department')
    } finally {
      setBusyId(null)
    }
  }

  const handleMove = async (index, direction) => {
    const reordered = swap(departments, index, direction)
    if (reordered === departments) return
    const previous = departments
    setDepartments(reordered)
    setReordering(true)
    try {
      const data = await reorderDepartments(
        token,
        authorityId,
        reordered.map((item) => item.id)
      )
      setDepartments(Array.isArray(data) ? data : reordered)
    } catch (error) {
      setDepartments(previous)
      if (error?.response?.status === 400) {
        toast.error('Order was out of sync — reloading the latest list.')
        load()
      } else {
        toastApiError(error, 'Failed to reorder departments')
      }
    } finally {
      setReordering(false)
    }
  }

  const visible = showInactive ? departments : departments.filter((item) => item.active)
  const canReorder = canWrite && showInactive
  const activeDepartments = departments.filter((item) => item.active)
  const missingCatchAll =
    activeDepartments.length > 0 && !activeDepartments.some((item) => item.is_catch_all)

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-3">
          <ToggleSwitch
            checked={showInactive}
            label="Show inactive"
            onChange={setShowInactive}
          />
          {!showInactive && canWrite && (
            <span className="text-xs text-gray-500 dark:text-gray-400">
              Show inactive to reorder.
            </span>
          )}
        </div>
        {canWrite && (
          <Button onClick={() => setModal({ open: true, mode: 'create', entity: null })}>
            <HiPlus className="mr-2 h-4 w-4" />
            Add Department
          </Button>
        )}
      </div>

      {canWrite && missingCatchAll && (
        <Alert color="warning" icon={HiExclamation}>
          No active catch-all department — reports that match no specific department won&apos;t
          route. Mark one department as catch-all.
        </Alert>
      )}

      {loading ? (
        <div className="flex items-center justify-center py-12">
          <Spinner size="xl" />
        </div>
      ) : visible.length === 0 ? (
        <p className="py-8 text-center text-sm text-gray-500 dark:text-gray-400">
          No departments{showInactive ? '' : ' active'} yet.
          {canWrite ? ' Add one to get started.' : ''}
        </p>
      ) : (
        <ul className="space-y-2">
          {visible.map((department) => {
            const fullIndex = departments.findIndex((item) => item.id === department.id)
            const expanded = expandedId === department.id
            const aorCount = department.areas_of_responsibility?.length ?? 0
            return (
              <li
                key={department.id}
                className={`rounded-lg border border-gray-200 dark:border-gray-700 ${
                  department.active ? 'bg-white dark:bg-gray-800' : 'bg-gray-50 dark:bg-gray-800/60'
                }`}
              >
                <div className="flex items-center gap-3 px-4 py-3">
                  {canReorder && (
                    <ReorderButtons
                      label={department.name}
                      isFirst={fullIndex === 0}
                      isLast={fullIndex === departments.length - 1}
                      disabled={reordering}
                      onUp={() => handleMove(fullIndex, -1)}
                      onDown={() => handleMove(fullIndex, 1)}
                    />
                  )}

                  <button
                    type="button"
                    className="flex min-w-0 flex-1 items-center gap-2 text-left"
                    onClick={() => setExpandedId(expanded ? null : department.id)}
                    aria-expanded={expanded}
                  >
                    {expanded ? (
                      <HiChevronDown className="h-4 w-4 shrink-0 text-gray-400" />
                    ) : (
                      <HiChevronRight className="h-4 w-4 shrink-0 text-gray-400" />
                    )}
                    <div className="min-w-0">
                      <div className="flex flex-wrap items-center gap-2">
                        <span className="truncate font-medium text-gray-900 dark:text-white">
                          {department.name}
                        </span>
                        <StatusBadges entity={department} />
                      </div>
                      <div className="mt-0.5 flex items-center gap-2 text-xs text-gray-500 dark:text-gray-400">
                        <span className="font-mono">{department.slug}</span>
                        <span aria-hidden>·</span>
                        <span>
                          {aorCount} area{aorCount === 1 ? '' : 's'} of responsibility
                        </span>
                      </div>
                    </div>
                  </button>

                  {canWrite && (
                    <div className="flex shrink-0 items-center gap-1">
                      <Button
                        size="xs"
                        color="gray"
                        disabled={busyId === department.id}
                        onClick={() => handleToggleActive(department)}
                      >
                        {department.active ? 'Deactivate' : 'Activate'}
                      </Button>
                      <Button
                        size="xs"
                        color="gray"
                        disabled={busyId === department.id}
                        onClick={() => setModal({ open: true, mode: 'edit', entity: department })}
                      >
                        <HiPencil className="h-3.5 w-3.5" />
                      </Button>
                      <Button
                        size="xs"
                        color="failure"
                        disabled={busyId === department.id}
                        onClick={() => handleDelete(department)}
                      >
                        <HiTrash className="h-3.5 w-3.5" />
                      </Button>
                    </div>
                  )}
                </div>

                {expanded && (
                  <div className="border-t border-gray-100 px-4 py-3 dark:border-gray-700">
                    <AorList
                      token={token}
                      authorityId={authorityId}
                      departmentId={department.id}
                      canWrite={canWrite}
                      showInactive={showInactive}
                      knownKeywords={knownKeywords}
                      duplicateKeys={duplicateKeys}
                      onAorsChange={handleAorsChange}
                    />
                  </div>
                )}
              </li>
            )
          })}
        </ul>
      )}

      <EntityFormModal
        open={modal.open}
        mode={modal.mode}
        entityLabel="Department"
        catchAllHint="The catch-all department receives reports that match no other department. Only one per authority — setting this clears it on the current catch-all."
        initial={modal.mode === 'edit' ? modal.entity : null}
        onClose={() => setModal({ open: false, mode: 'create', entity: null })}
        onSubmit={modal.mode === 'edit' ? handleEdit : handleCreate}
      />
    </div>
  )
}

export default DepartmentList
